import { resolveMediaUrl } from '../../helpers/userAvatar'
import { getXpProgress } from '../../helpers/xpLevel'
import cityBannerFallback from '../../assets/images/city-hub/dallas-banner.jpg'

const NA = 'N/A'

const ADDRESS_TYPE_ORDER = ['primary', 'secondary', 'tertiary']

const ADDRESS_TYPE_LABELS = {
  primary: 'Primary City',
  secondary: 'Secondary City',
  tertiary: 'Tertiary City',
}

const TIER_LABELS = {
  primary: 'Primary',
  secondary: 'Secondary',
  tertiary: 'Tertiary',
}

/**
 * Owned-city City Hub access unlocks with XP level:
 * Level 1 → Primary, Level 2 → Secondary, Level 3 → Tertiary.
 */
export const CITY_TIER_REQUIRED_LEVEL = {
  primary: 1,
  secondary: 2,
  tertiary: 3,
}

export const getCityTierRequiredLevel = (tierOrAddressType = null) => {
  const key = String(tierOrAddressType || '')
    .trim()
    .toLowerCase()
  return CITY_TIER_REQUIRED_LEVEL[key] || null
}

export const isCityTierAccessible = (
  tierOrAddressType = null,
  userLevel = 1,
) => {
  const required = getCityTierRequiredLevel(tierOrAddressType)
  if (required == null) return true
  return Math.max(1, Number(userLevel) || 1) >= required
}

export const isOwnedCityHubAccessible = (city = null, userLevel = 1) => {
  if (!city?.isOwned && !city?.addressType && !city?.tier) return true
  const tier = city?.addressType || city?.tier
  return isCityTierAccessible(tier, userLevel)
}

export const getUserCityHubLevel = (user = null) =>
  getXpProgress(user).xpLevel

const displayOrNa = value => {
  if (value == null) return NA
  const text = String(value).trim()
  return text || NA
}

export const formatCityHubName = city => {
  if (!city) return NA
  if (typeof city === 'string') return displayOrNa(city)

  const name = city.city || city.name || ''
  const state = city.state || ''
  if (name && state) return `${name}, ${state}`
  return displayOrNa(name)
}

export const getCityShortName = cityName => {
  const name = displayOrNa(cityName)
  if (name === NA) return NA
  return name.split(',')[0].trim() || NA
}

const resolveCityBanner = (city, fallbackBanner = cityBannerFallback) =>
  resolveMediaUrl(city?.cover_media_url) ||
  resolveMediaUrl(city?.thumbnail_path_url) ||
  resolveMediaUrl(city?.cover_image) ||
  resolveMediaUrl(city?.thumb) ||
  fallbackBanner

/**
 * Map a public/API city into City Hub banner view model.
 */
export const mapApiCityToCityHubCity = (
  city,
  { addressType = null, typeLabel = null } = {}
) => {
  const name = formatCityHubName(city)
  const normalizedType = String(addressType || '')
    .trim()
    .toLowerCase()

  return {
    id: city?.id || null,
    slug: city?.slug || null,
    name,
    shortName: getCityShortName(name),
    type:
      typeLabel ||
      ADDRESS_TYPE_LABELS[normalizedType] ||
      (normalizedType ? 'City' : 'Visiting'),
    addressType: normalizedType || null,
    tier: normalizedType || null,
    tierLabel: TIER_LABELS[normalizedType] || '',
    tagline:
      displayOrNa(city?.tagline) !== NA
        ? city.tagline
        : 'Discover, connect, and create opportunities in your city.',
    banner: resolveCityBanner(city),
    thumb:
      resolveMediaUrl(city?.thumbnail_path_url) ||
      resolveMediaUrl(city?.cover_media_url) ||
      resolveCityBanner(city),
    description: displayOrNa(city?.description),
    subtitle:
      [city?.state, city?.country].filter(Boolean).join(', ') || null,
    rawCity: city || null,
    rawAddress: null,
    isOwned: Boolean(normalizedType),
  }
}

/**
 * Map a user address (+ nested city) into City Hub banner view model.
 */
export const mapAddressToCityHubCity = (
  address,
  fallbackBanner = cityBannerFallback
) => {
  const city = address?.city || null
  const addressType = String(address?.address_type || '')
    .trim()
    .toLowerCase()
  const mapped = mapApiCityToCityHubCity(city, { addressType })

  return {
    ...mapped,
    id: city?.id || address?.city_id || mapped.id,
    banner: mapped.banner || fallbackBanner,
    rawAddress: address || null,
    isOwned: true,
  }
}

export const getUserCityAddresses = (user = null) => {
  const addresses = Array.isArray(user?.addresses) ? user.addresses : []
  return addresses.filter(address => address?.city || address?.city_id)
}

export const mapUserAddressesToExploreCities = (user = null) => {
  const byType = {}
  getUserCityAddresses(user).forEach(address => {
    const type = String(address?.address_type || '')
      .trim()
      .toLowerCase()
    if (ADDRESS_TYPE_ORDER.includes(type) && !byType[type]) {
      byType[type] = mapAddressToCityHubCity(address)
    }
  })

  return ADDRESS_TYPE_ORDER.map(type => byType[type]).filter(Boolean)
}

export const getUserOwnedCityIds = (user = null) =>
  new Set(
    mapUserAddressesToExploreCities(user)
      .map(city => String(city.id))
      .filter(Boolean)
  )

/** True when the current user lists this city among their owned addresses. */
export const isCityHubMember = (cityId, yourCities = []) =>
  Boolean(cityId) &&
  (Array.isArray(yourCities) ? yourCities : []).some(
    item => String(item?.id) === String(cityId),
  )

/**
 * Full City Hub tools (create/contribute) — owned city unlocked by XP level.
 * Pre-unlock owned cities are visit/browse only.
 */
export const hasFullCityHubAccess = (
  cityId,
  yourCities = [],
  userLevel = 1,
) => {
  const match = findCityHubCityById(cityId, yourCities)
  if (!match) return false
  return isOwnedCityHubAccessible(match, userLevel)
}

const MEMBER_ENGAGE_COPY = {
  canEngage: false,
  reason: "membership",
  context: "Only members of this city can respond.",
  placeholder: "Join this city to respond",
  hint: "Only members of this city can like, reply, or respond",
}

/**
 * Why call responses / likes are blocked in City Hub detail.
 * Distinguishes non-members from owned Secondary/Tertiary cities still locked by XP level.
 */
export const getCityHubEngageRestriction = (
  cityId,
  yourCities = [],
  userLevel = 1,
) => {
  if (!cityId) return { ...MEMBER_ENGAGE_COPY }

  if (hasFullCityHubAccess(cityId, yourCities, userLevel)) {
    return {
      canEngage: true,
      reason: null,
      context: null,
      placeholder: "Write a response...",
      hint: undefined,
    }
  }

  const match = findCityHubCityById(cityId, yourCities)
  if (match && !isOwnedCityHubAccessible(match, userLevel)) {
    const tier = match.addressType || match.tier
    const requiredLevel = getCityTierRequiredLevel(tier) || 2
    const tierLabel = TIER_LABELS[String(tier || "").toLowerCase()] || "this"
    return {
      canEngage: false,
      reason: "level",
      context: `Reach Level ${requiredLevel} to respond in your ${tierLabel} city.`,
      placeholder: `Unlock at Level ${requiredLevel}`,
      hint: `Your ${tierLabel} city unlocks at Level ${requiredLevel}. You can browse until then.`,
    }
  }

  return { ...MEMBER_ENGAGE_COPY }
}

/** Present an owned city as visit/browse until its tier unlocks. */
export const toVisitModeCityHubCity = (city = null) => {
  if (!city) return city
  return {
    ...city,
    type: 'Visiting',
    isOwned: false,
    isVisitOnly: true,
  }
}

/**
 * Owned + unlocked → full city. Owned + locked tier → visit mode.
 * Non-owned / other cities pass through unchanged.
 */
export const resolveCityHubCityForLevel = (city = null, userLevel = 1) => {
  if (!city) return city
  const isOwnedCity = Boolean(city.isOwned || city.addressType || city.tier)
  if (!isOwnedCity) return city
  if (isOwnedCityHubAccessible(city, userLevel)) {
    return { ...city, isVisitOnly: false }
  }
  return toVisitModeCityHubCity(city)
}

export const pickDefaultCityHubCity = (user = null) => {
  const owned = mapUserAddressesToExploreCities(user)
  if (owned.length === 0) {
    return mapAddressToCityHubCity(null)
  }

  const userLevel = getUserCityHubLevel(user)
  const accessible = owned.filter(city =>
    isOwnedCityHubAccessible(city, userLevel),
  )

  return (
    accessible.find(city => city.addressType === 'primary') ||
    accessible[0] ||
    owned.find(city => city.addressType === 'primary') ||
    owned[0]
  )
}

export const findCityHubCityById = (cityId, yourCities = []) => {
  if (!cityId) return null
  return (
    yourCities.find(item => String(item.id) === String(cityId)) || null
  )
}

/** Resolve an owned city only when the user's level unlocks that tier. */
export const findAccessibleOwnedCityHubCity = (
  cityId,
  yourCities = [],
  userLevel = 1,
) => {
  const match = findCityHubCityById(cityId, yourCities)
  if (!match) return null
  if (!isOwnedCityHubAccessible(match, userLevel)) return null
  return match
}

/** Resolve City Hub city model from a call payload (uses `cities` relation). */
export const mapCallToCityHubCity = (call, cityId) => {
  const source = call?.raw || call || {}
  const cities = Array.isArray(source.cities) ? source.cities : []
  const match =
    cities.find(item => String(item?.id) === String(cityId)) || cities[0]
  if (!match?.id) return null

  const ownedType = String(match?.pivot?.address_type || "")
    .trim()
    .toLowerCase()

  return mapApiCityToCityHubCity(match, {
    addressType: ownedType || null,
    typeLabel: ownedType ? undefined : "Visiting",
  })
}
